<script>
    import Chart from "chart.js/auto";
    import chartService from "../../../services/chart.js";
    import Gradient from "../../../services/gradient.js";
    import { truncateString } from "../../../js/utils.js";
    import { format } from "svelte-i18n";
    import { params, goto } from "@roxi/routify";

    let {
        data = [],
        field = "value",
        type = "",
        propertyKind = "",
        linkTo,
        filter = {},
    } = $props();

    const redirect = $goto;

    let propertyName = $derived(
        $params.name ? decodeURIComponent($params.name) : null,
    );

    let canvas = $state();
    let chart;

    let processedData = $derived.by(() => {
        let dataCopy = JSON.parse(JSON.stringify(data || []));
        let tooltips = {};

        if (dataCopy.length >= 10) {
            let otherResults = dataCopy.splice(9);
            let result = otherResults.reduce((accumulator, currentObject) => {
                return accumulator + (currentObject.count || 0);
            }, 0);
            dataCopy.push({
                count: result,
                value: $format("others"),
                name: $format("others"),
                kind: $format("others"),
            });
        }

        if (dataCopy && dataCopy.length) {
            dataCopy.sort((a, b) => {
                if (a.name == $format("others")) return 1;
                else if (b.name == $format("others")) return -1;
                else return b.count - a.count;
            });
        }

        let gradientArray = new Gradient()
            .setColorGradient("#343a40", "#3c8dbc")
            .setMidpoint(dataCopy.length <= 5 ? 5 : dataCopy.length)
            .getColors();

        for (let [index, color] of gradientArray.entries()) {
            gradientArray[index] = color + "AE";
        }

        let chartLabels = [];
        let chartValues = [];

        for (const obj of dataCopy) {
            if (obj.value === "null" || obj.value === "" || !obj.value)
                obj.value = "N/A";
            if (obj.label && obj.label.includes("null")) {
                obj.label = obj.label.replace("null", "N/A");
            }
            let title = obj.label ? obj.label : obj[field];
            title = truncateString(title, 25);
            chartLabels.push(title);
            tooltips[title] = obj.label ? obj.label : obj[field];
            chartValues.push(obj.count);
        }

        return {
            gradientArray,
            chartLabels,
            chartValues,
            tooltips,
        };
    });

    $effect(() => {
        if (!canvas) return;

        if (chart) {
            chart.destroy();
            chart = null;
        }

        let context = canvas.getContext("2d");
        let { gradientArray, chartLabels, chartValues, tooltips } =
            processedData;

        chart = new Chart(context, {
            type: "doughnut",
            options: {
                responsive: true,
                maintainAspectRatio: false,
                borderWidth: 1,
                weight: 20,
                plugins: {
                    legend: {
                        position: "right",
                        labels: {
                            font: {
                                fontFamily: "Montserrat",
                                size: 14,
                            },
                        },
                    },
                    tooltip: {
                        callbacks: {
                            title: function (tooltipItem) {
                                return (
                                    tooltips[tooltipItem[0].label] ||
                                    tooltipItem[0].label
                                );
                            },
                        },
                        fontFamily: "Montserrat",
                    },
                },
                animation: {
                    animateScale: true,
                    animateRotate: true,
                },
                onHover: (event) => {
                    canvas.style.cursor = "pointer";
                },
                onClick: (event) => {
                    let cleanFilter = { ...(filter || {}) };
                    delete cleanFilter.kind;
                    delete cleanFilter.name;

                    const clickedLabel = event.chart.tooltip.title[0];
                    if (!clickedLabel || clickedLabel == $format("others"))
                        return;

                    const apacheEncode = (val) => val ? encodeURIComponent(val) : "";

                    if (!field || field != "value") {
                        if (propertyKind) {
                            redirect(`/property/${type}/[kind]/[name]`, {
                                kind: apacheEncode(propertyKind),
                                name: apacheEncode(clickedLabel),
                                ...cleanFilter,
                            });
                        } else {
                            redirect(`/property/${type}/[kind]`, {
                                kind: apacheEncode(clickedLabel),
                                ...cleanFilter,
                            });
                        }
                    } else {
                        if (linkTo == "values") {
                            if (clickedLabel == "N/A") return;

                            if (clickedLabel.includes(" - ")) {
                                let parts = clickedLabel.split(" - ");
                                let nameVal = parts[0];
                                let val = parts[1];
                                if (
                                    !val ||
                                    val == "N/A" ||
                                    val == "null" ||
                                    val == $format("others")
                                ) {
                                    return;
                                }
                                redirect(
                                    `/property/${type}/[kind]/[name]/values`,
                                    {
                                        kind: apacheEncode(propertyKind),
                                        name: apacheEncode(nameVal),
                                        value: apacheEncode(val),
                                        ...cleanFilter,
                                    },
                                );
                            } else {
                                redirect(
                                    `/property/${type}/[kind]/[name]/values`,
                                    {
                                        kind: apacheEncode(propertyKind),
                                        name: apacheEncode(propertyName),
                                        value: apacheEncode(clickedLabel),
                                        ...cleanFilter,
                                    },
                                );
                            }
                        } else if (linkTo == "name") {
                            if (clickedLabel.includes(" - ")) {
                                let parts = clickedLabel.split(" - ");
                                let kindVal = parts[0];
                                let nameVal = parts[1];
                                if (
                                    !nameVal ||
                                    nameVal == "N/A" ||
                                    nameVal == "null" ||
                                    nameVal == $format("others")
                                ) {
                                    return;
                                }
                                redirect(`/property/${type}/[kind]/[name]`, {
                                    kind: apacheEncode(kindVal),
                                    name: apacheEncode(nameVal),
                                    ...cleanFilter,
                                });
                            }
                        } else {
                            redirect(`/property/${type}/[kind]/[name]/values`, {
                                kind: apacheEncode(propertyKind),
                                name: apacheEncode(propertyName),
                                value: apacheEncode(clickedLabel),
                                ...cleanFilter,
                            });
                        }
                    }
                },
            },
            data: {
                labels: chartLabels,
                datasets: [
                    {
                        backgroundColor: gradientArray,
                        data: chartValues,
                    },
                ],
            },
        });

        return () => {
            if (chart) {
                chart.destroy();
                chart = null;
            }
        };
    });
</script>

<canvas
    bind:this={canvas}
    id="chart-vuls-by-severity"
    class:chart-has-no-data={!data || data.length <= 0}
></canvas>

<style>
</style>